import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { LogInIcon, MailIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { resendInvitationWithToken, sendSsoRecoveryLink } from 'sdk';
import { isStrategyEnabled } from 'shared';
import { useAuthStore } from '~/modules/auth/auth-store';
import { ErrorNotice } from '~/modules/common/error-notice';
import { ResendInvitationButton } from '~/modules/memberships/resend-invitation-button';
import { Button } from '~/modules/ui/button';

const magicLinkEnabled = isStrategyEnabled('magic');

export function AuthErrorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { error: errorType, tokenId } = useSearch({ from: '/_public/auth/error' });

  const setMagicLinkSent = useAuthStore((state) => state.setMagicLinkSent);

  // Resending needs the expired invitation's token id: an address alone would tell anyone who was invited.
  const resendTokenId = errorType === 'invitation_expired' ? tokenId : undefined;
  // Any other refusal of an invitation round trip (a provider account on another address) resumes the invitation.
  const resumeTokenId = resendTokenId ? undefined : tokenId;

  // The address has an account or the institution does not prove it: this browser may ask once for a sign-in link, which opens the account page to connect the institution.
  const offersRecoveryLink = (errorType === 'sso_email_exists' || errorType === 'sso_email_unproven') && magicLinkEnabled;
  const { mutate: sendRecoveryLink, isPending: isSendingRecoveryLink } = useMutation({
    mutationFn: () => sendSsoRecoveryLink(),
    onSuccess: ({ email }) => {
      setMagicLinkSent(email, 'signin');
      navigate({ to: '/auth/authenticate', replace: true });
    },
  });

  const hasPrimaryAction = !!resendTokenId || offersRecoveryLink;

  return (
    <ErrorNotice boundary="public">
      {resendTokenId && <ResendInvitationButton send={() => resendInvitationWithToken({ body: { tokenId: resendTokenId } })} />}

      {offersRecoveryLink && (
        <Button loading={isSendingRecoveryLink} onClick={() => sendRecoveryLink()}>
          <MailIcon />
          {t('c:sso_recovery_send')}
        </Button>
      )}

      <Button
        variant={hasPrimaryAction ? 'secondary' : 'default'}
        render={<Link to="/auth/authenticate" search={resumeTokenId ? { tokenId: resumeTokenId } : {}} replace />}
      >
        <LogInIcon />
        {t('c:sign_in')}
      </Button>
    </ErrorNotice>
  );
}
